import { useContext, useEffect, useState } from "react";
import APIContext from "../../hooks/API";
import { useParams } from "react-router-dom";
import Actions from "./Actions/actions";
import { ContainerWithSwitchHeader, ContainerBody } from "../Container";
import { T } from "../traks";
import OrderEvents from "./OrderEvents";

const ShowOrder = () => {
	const [order, setOrder] = useState();
	const { companyId, orderId } = useParams();
	const { get, setCompanyId } = useContext(APIContext);
	const [reload, setReload] = useState(false);
	useEffect(() => {
		if (orderId && companyId) {
			setCompanyId(companyId);
			get({ path: `/order/${orderId}` }).then((e) => {
				if (e) setOrder(e);
			});
		}
	}, [companyId, get, orderId, setCompanyId, setOrder, reload]);
	if (!order) return null;
	const formatter = new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: order.details.currency,
	});
	const shippingaddress = order.details.shippingaddress;
	const billingaddress = order.details.billingaddress;
	return (
		<div className="order-detail">
			<h2 style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "1.5rem" }}>
				<T>Order {order.orderId}</T>
				<span className="order-status-badge">{order.orderStatus}</span>
			</h2>

			<div className="app-card">
				<div className="app-card-header"><T>Actions</T></div>
				<div className="app-card-body">
					<Actions order={order} setReload={setReload} />
				</div>
			</div>

			<div className="app-card">
				<div className="app-card-header"><T>Summary</T></div>
				<div className="app-card-body">
					<div className="order-totals" style={{ marginBottom: "1.25rem" }}>
						<div className="order-total-item">
							<span className="order-total-label"><T>Total</T></span>
							<span className="order-total-value">
								{formatter.format(order.details.amount / 100)}
							</span>
						</div>
						<div className="order-total-item">
							<span className="order-total-label"><T>VAT</T></span>
							<span className="order-total-value">
								{formatter.format(order.details?.vatamount / 100 || 0)}
							</span>
						</div>
					</div>

					<Timeline order={order} />
				</div>
			</div>

			{(shippingaddress || billingaddress) && (
				<div className="app-card">
					<div className="app-card-header"><T>Addresses</T></div>
					<div className="app-card-body">
						<div className="address-grid">
							{!!shippingaddress && (
								<AddressBlock address={shippingaddress} title="Shipping Address" />
							)}
							{!!billingaddress && (
								<AddressBlock address={billingaddress} title="Billing Address" />
							)}
						</div>
					</div>
				</div>
			)}

			<div className="app-card">
				<div className="app-card-header"><T>Items</T></div>
				<div className="app-card-body" style={{ padding: 0 }}>
					<ItemsTable items={order.details.lines} formatter={formatter} />
				</div>
			</div>

			<div className="app-card">
				<div className="app-card-header"><T>Events</T></div>
				<div className="app-card-body">
					<OrderEvents events={order.events} />
				</div>
			</div>

			<ContainerWithSwitchHeader
				initialShow={false}
				headerText={<T>Raw data</T>}
				nobreak
			>
				<ContainerBody>
					<pre style={{ fontSize: "0.8rem", margin: 0, whiteSpace: "pre-wrap" }}>
						{JSON.stringify(order, undefined, 2)}
					</pre>
				</ContainerBody>
			</ContainerWithSwitchHeader>
		</div>
	);
};

const Timeline = ({ order }) => {
	let TsKeys = Object.keys(order).filter((k) => k.includes("Ts") && order[k]);
	const dates = TsKeys.reduce((p, c) => ({ ...p, [c]: new Date(order[c]) }), {});

	TsKeys = TsKeys.sort((a, b) => dates[a] >= dates[b]);
	let intervals = TsKeys.map((d, i) => [
		d.slice(0, -2),
		!i || dates[d].getTime() - dates.createdTs.getTime(),
	]);

	return (
		<div className="timeline">
			<div className="timeline-item">
				<span className="timeline-label">
					Created
				</span>
				<span className="timeline-offset">
					{dates.createdTs.toLocaleString()}
				</span>
			</div>
			{intervals.map(
				(v, i) =>
					i > 0 && (
						<div className="timeline-item" key={`interval-${i}`}>
							<span className="timeline-label">{v[0]}</span>
							<span className="timeline-offset">{intervalStr(v[1])}</span>
						</div>
					)
			)}
		</div>
	);
};

const intervalStr = (interval) => {
	let d = Math.floor(interval / (1000 * 60 * 60 * 24));
	let h = Math.floor((interval % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
	let m = Math.floor((interval % (1000 * 60 * 60)) / (1000 * 60));
	let s = Math.floor((interval % (1000 * 60)) / 1000);
	const toStr = (val, unit) => (val || unit === "s" ? `${val}${unit} ` : "");
	return `+${toStr(d, "d")}${toStr(h, "h")}${toStr(m, "m")}${toStr(s, "s")}`;
};

const ItemsTable = ({ items, formatter }) => (
	<table className="items-table">
		<thead>
			<tr>
				<th><T>Description</T></th>
				<th><T>Qty</T></th>
				<th><T>Unit net</T></th>
				<th><T>Unit gross</T></th>
				<th><T>VAT</T></th>
			</tr>
		</thead>
		<tbody>
			{items.map((item) => (
				<tr key={item.id}>
					<td>
						<div style={{ fontWeight: 500 }}>{item.description}</div>
						{item.text && (
							<div style={{ fontSize: "0.8rem", color: "#8c98a9" }}>
								{item.text}
							</div>
						)}
					</td>
					<td>
						{item.quantity}{item.unit ? ` ${item.unit}` : ""}
					</td>
					<td>{formatter.format(item.unitprice / 100)}</td>
					<td>{formatter.format(item.unitpriceinclvat / 100)}</td>
					<td>
						{item.vatrate}%
						{item.vattype && (
							<span style={{ fontSize: "0.75rem", color: "#8c98a9", marginLeft: 4 }}>
								({item.vattype})
							</span>
						)}
					</td>
				</tr>
			))}
		</tbody>
	</table>
);

const AddressBlock = ({ address, title }) => {
	let hasValues = Object.values(address).reduce((p, c) => p + c, "");
	if (!hasValues) return null;
	return (
		<div className="address-block">
			<h6>{title}</h6>
			<p>
				<ConditionalLine value={address.att} prefix="Att.: " />
				<ConditionalLine
					value={
						(address.firstname || address.lastname) &&
						`${address.firstname || ""} ${address.lastname || ""}`.trim()
					}
				/>
				<ConditionalLine value={address.street} />
				<ConditionalLine
					value={
						(address.zip || address.city) &&
						`${address.zip || ""} ${address.city || ""}`.trim()
					}
				/>
				<ConditionalLine value={address.state} />
				<ConditionalLine value={address.country} />
				{(address.homephonenumber || address.workphonenumber || address.email) && (
					<>
						<br />
						<ConditionalLine value={address.homephonenumber} prefix="Home: " />
						<ConditionalLine value={address.workphonenumber} prefix="Work: " />
						<ConditionalLine value={address.email} />
					</>
				)}
			</p>
		</div>
	);
};

const ConditionalLine = ({ value, prefix }) =>
	!!value && (
		<>
			{prefix}{value}<br />
		</>
	);

export default ShowOrder;
